import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { validateLoginForm, getUserFromStorage, handleLoginSuccess } from '../../../utils/manualLoginUtils';

/**
 * Custom hook for managing manual login form state and submission
 * @returns {Object} Form state and handlers
 */
export const useManualLoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleRememberMeChange = (checked) => {
    setFormData({
      ...formData,
      rememberMe: checked,
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate form
    const validation = validateLoginForm(formData.email, formData.password);
    if (!validation.isValid) {
      setError(validation.error);
      setLoading(false);
      return;
    }

    // Attempt login
    const result = await login(formData.email, formData.password);

    if (result.success) {
      setError('');
      const user = getUserFromStorage();
      handleLoginSuccess(user, navigate);
    } else {
      if (result.status === 401) {
        if (Number.isFinite(result.remainingAttempts)) {
          setError(`Invalid email or password. Attempts remaining: ${result.remainingAttempts}.`);
        } else {
          setError('Invalid email or password.');
        }
      } else if (result.status === 429) {
        const seconds = Number.isFinite(result.retryAfterSeconds)
          ? Math.max(1, Math.floor(result.retryAfterSeconds))
          : null;
        if (seconds) {
          const minutes = Math.floor(seconds / 60);
          const remainingSeconds = seconds % 60;
          const parts = [];
          if (minutes > 0) parts.push(`${minutes}m`);
          parts.push(`${remainingSeconds}s`);
          setError(`Too many login attempts. Please try again in ${parts.join(' ')}.`);
        } else {
          setError('Too many login attempts. Please try again after some time.');
        }
      } else {
        setError('Login failed. Please try again later.');
      }
    }

    setLoading(false);
  };

  return {
    formData,
    error,
    loading,
    showPassword,
    handleChange,
    handleRememberMeChange,
    togglePasswordVisibility,
    handleSubmit,
  };
};
